import { useEffect, useRef, useState, type ReactNode } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { Modal } from "@/components/modal";
import { Form, FormSubmit } from "@/src/forms";
import { fetchDpaTemplateBytes } from "@/src/lib/dpa-pdf";
import { formatDate } from "@/app/(app)/_lib/format";
import type { DataRetentionPeriod } from "@/src/services/company_service";
import { signDpaSchema, type SignDpaValues } from "../_schemas/sign-dpa";
import { RetentionPeriodPicker } from "./retention-period-picker";

type Props = {
  open: boolean;
  onClose: () => void;
  onSigned: (result: { pdfBytes: Uint8Array; period: DataRetentionPeriod }) => void;
  signerName: string;
  signerEmail: string;
  insetLeft?: number;
};

function ConsentCheckbox({
  checked,
  onChange,
  error,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="flex cursor-pointer items-start gap-2 text-small text-grey-900">
        <span
          aria-hidden
          className={[
            "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-[2px] border transition-colors",
            checked
              ? "border-green-600 bg-green-500 text-white"
              : "border-grey-300 bg-white text-transparent",
          ].join(" ")}
        >
          <Icon name="check" size={20} />
        </span>
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{children}</span>
      </label>
      {error && <p className="pl-7 text-tiny text-red-600">{error}</p>}
    </div>
  );
}

function SignSubmitButton() {
  const { control } = useFormContext<SignDpaValues>();
  const [signerConfirmed, adminOrOwnerConfirmed] = useWatch({
    control,
    name: ["signerConfirmed", "adminOrOwnerConfirmed"],
  });
  const canSign = !!signerConfirmed && !!adminOrOwnerConfirmed;

  return (
    <FormSubmit hierarchy="primary" pendingLabel="Signing…" className="w-full" disabled={!canSign}>
      Sign & submit
    </FormSubmit>
  );
}

export function SignDpaModal({
  open,
  onClose,
  onSigned,
  signerName,
  signerEmail,
  insetLeft,
}: Props) {
  const [templateBytes, setTemplateBytes] = useState<Uint8Array | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [period, setPeriod] = useState<DataRetentionPeriod>("day");
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) setPeriod("day");
  }, [open]);

  useEffect(() => {
    if (!open || templateBytes) return;
    let cancelled = false;
    fetchDpaTemplateBytes().then(
      (bytes) => {
        if (cancelled) return;
        setLoadError(null);
        setTemplateBytes(bytes);
        const url = URL.createObjectURL(
          new Blob([new Uint8Array(bytes)], { type: "application/pdf" }),
        );
        previewUrlRef.current = url;
        setPreviewUrl(url);
      },
      (err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Couldn't load the DPA template");
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, templateBytes]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  function handleValid() {
    if (!templateBytes) return;
    onSigned({ pdfBytes: templateBytes, period });
  }

  const today = formatDate(new Date());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sign the Data Processing Agreement"
      description="Review the agreement, then confirm below to enable data retention."
      width={1200}
      insetLeft={insetLeft}
    >
      <div className="@container">
        <div className="flex flex-col gap-6 @3xl:flex-row">
          <div className="min-w-0 flex-1">
            {loadError && (
              <Alert tone="danger" role="alert">
                {loadError}
              </Alert>
            )}
            {!loadError && !previewUrl && (
              <p className="text-small text-grey-600">Loading agreement…</p>
            )}
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Data Processing Agreement"
                className="h-[75vh] w-full rounded-sm border border-grey-200"
              />
            )}
          </div>

          {templateBytes && (
            <Form
              schema={signDpaSchema}
              defaultValues={{ signerConfirmed: false, adminOrOwnerConfirmed: false }}
              onSubmit={handleValid}
              className="flex w-full flex-col gap-4 rounded-sm border border-grey-200 bg-grey-25 p-4 @3xl:w-[340px] @3xl:shrink-0"
            >
              <div className="flex items-center gap-2 border-b border-grey-200 pb-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-green-500-12 text-green-700">
                  <Icon name="draw" size={20} />
                </div>
                <div className="flex flex-col">
                  <span className="text-body font-semibold text-black">Confirm & sign</span>
                  <span className="text-tiny text-grey-600">Both confirmations are required</span>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 text-small">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-grey-600">Signing as</span>
                  <span className="font-medium text-grey-900">{signerName}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-grey-600">Email</span>
                  <span className="font-medium text-grey-900">{signerEmail}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-grey-600">Date</span>
                  <span className="font-medium text-grey-900">{today}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 border-t border-grey-200 pt-4">
                <span className="text-tiny font-semibold uppercase tracking-wide text-grey-500">
                  Retention period
                </span>
                <RetentionPeriodPicker value={period} onChange={setPeriod} size="small" />
              </div>

              <div className="flex flex-col gap-3 border-t border-grey-200 pt-4">
                <Controller
                  name="signerConfirmed"
                  render={({ field, fieldState }) => (
                    <ConsentCheckbox
                      checked={!!field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    >
                      I am electronically signing this Data Processing Agreement on behalf of the
                      company.
                    </ConsentCheckbox>
                  )}
                />
                <Controller
                  name="adminOrOwnerConfirmed"
                  render={({ field, fieldState }) => (
                    <ConsentCheckbox
                      checked={!!field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    >
                      I hold Administrator or Owner permissions on this account at the time of
                      signing.
                    </ConsentCheckbox>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2 border-t border-grey-200 pt-4">
                <SignSubmitButton />
                <Button hierarchy="secondary" type="button" onClick={onClose} className="w-full">
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </Modal>
  );
}
